import React, { useEffect, useReducer, useState, useMemo, useContext } from 'react'
import { Router, Switch, Route, Redirect } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { setTheme } from 'themes/utils'
import messages from './messages'
import CustomerOrdersPage from 'containers/CustomerOrdersPage'
import CustomerBillingPage from 'containers/CustomerBillingPage'
import CustomerOrderUpsertPage from 'containers/CustomerOrderUpsertPage'
import CustomerVoiceSelectorsPage from 'containers/CustomerVoiceSelectorsPage'
import Home from 'containers/CustomerHomePage'
import SidebarLayout from 'components/SidebarLayout'
import CustomerSettingsPage from 'containers/CustomerSettingsPage'
import CustomerTeamPage from 'containers/CustomerTeamPage'
import UserSettingsPage from 'containers/UserSettingsPage'
import CustomerRosterPage from 'containers/CustomerRosterPage'
import CustomerReportsPage from 'containers/CustomerReportsPage'
import DocumentationPage from 'containers/DocumentationPage'
import PublicCustomerOrderPage from 'containers/PublicCustomerOrderPage'
import PublicPayPalPaymentPage from 'containers/PublicPayPalPaymentPage'
import PublicLiveSessionRedirectPage from 'containers/PublicLiveSessionRedirectPage'
import AuditionScopePage from 'containers/AuditionScopePage'
import CustomerFeedbackPage from 'containers/CustomerFeedbackPage'
import useNotifications from 'hooks/useNotifications'
import { CustomerChat } from 'components/SupportChat'
import { IntlContext, locale } from 'App'

export const BrandContext = React.createContext({})

export const CustomerApp = ({ partner, history, userId }) => {
  const [brand, setBrand] = useReducer((state, action) => action, {})
  const notifications = useNotifications(userId)
  const { dispatch } = useContext(IntlContext)
  const { formatMessage } = useIntl()

  useEffect(() => {
    fetch(`/rest/v2/customer/brand`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then(({ success, data }) => {
        if (success) {
          const { messages: intlMessages, theme, ...brand } = data

          if (theme) {
            setTheme(theme)
          }

          if (intlMessages) {
            dispatch({ action: 'OVERWRITE', payload: intlMessages })
          }

          setBrand({ ...brand })

          document.title = `${brand.displayName} x VoiceArchive`
        }
      })
  }, [])

  let routes = [
    { path: '/home', name: formatMessage(messages.navHome), icon: 'home' },
    { path: '/orders', name: formatMessage(messages.navOrders), icon: 'folders' },
    { path: '/company/team', name: formatMessage(messages.navTeam), icon: 'user-friends' },
    { path: '/roster', name: formatMessage(messages.navRoster), icon: 'list-music' },
    { path: '/reports', name: formatMessage(messages.navReports), icon: 'chart-bar' },
    { path: '/billing/history', name: formatMessage(messages.navBilling), icon: 'receipt' },
    { path: '/voiceselectors', name: formatMessage(messages.navVoiceSelectors), icon: 'share-alt' },
    { path: '/docs', name: formatMessage(messages.navTraining), icon: 'helicopter' },
    { path: '/orders/new', name: formatMessage(messages.navNewOrder), icon: 'plus', cta: true },
    { path: '/voices/search', name: formatMessage(messages.navSearchVoices), icon: 'search', cta: true, href: formatMessage(messages.navSearchVoicesHref) },
  ]

  if (brand && brand.routes) {
    routes = routes.filter((route) => brand.routes.includes(route.path))
  } else {
    routes = routes.filter((route) => ['/home'].includes(route.path))
  }

  return (
    <BrandContext.Provider value={{ brand, dispatch: setBrand }}>
      <Router history={history} basename="/studio">
        <SidebarLayout brand={brand} routes={routes} notifications={notifications}>
          <Switch>
            <Route path="/s/:hashId" component={PublicCustomerOrderPage} />
            <Route path="/paypal/:hashId" component={PublicPayPalPaymentPage} />
            <Route path="/ls/:hashId" component={PublicLiveSessionRedirectPage} />
            <Route path="/company/settings" component={CustomerSettingsPage} />
            <Route path="/company/team" component={CustomerTeamPage} />
            <Route path="/settings" component={UserSettingsPage} />
            <Route path="/roster" component={CustomerRosterPage} />
            <Route path="/reports" component={CustomerReportsPage} />
            <Route path="/orders/:id/overview" render={(props) => (
              <AuditionScopePage orderId={props.match.params.id} />
            )} />
            <Route path="/feedback/:hash" component={CustomerFeedbackPage} />
            <Route path="/orders/*" component={CustomerOrderUpsertPage} />
            <Route path="/orders" component={CustomerOrdersPage} />
            <Route path="/billing/history" component={CustomerBillingPage} />
            <Route path="/docs" component={DocumentationPage} />
            <Route path="/voiceselectors" component={CustomerVoiceSelectorsPage} />
            <Route 
              path="/home" 
              render={() => (
                <Home notifications={notifications}/>
              )}
            />
            <Redirect to="/home" />
          </Switch>
        </SidebarLayout>
      </Router>

      { brand.voiceflow === false && partner === 'voicearchive' &&
        <CustomerChat />
      }
    </BrandContext.Provider>
  )
}

export default CustomerApp
